import { forwardRef, useState } from "react";
// @mui
import { useTheme } from "@mui/material/styles";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import ListItemText from "@mui/material/ListItemText";
// routes
import { RouterLink } from "../../../routes/components";
//
import Iconify from "../../iconify";
//
import { NavItemProps, NavConfigProps } from "../types";
import { StyledItem, StyledIcon } from "./styles";
import LogoutDialog from "../../custom-dialog/logout-dialog";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { doLogout } from "../../../store/auth/auth.action";
import { useNavigate } from "react-router";

// ----------------------------------------------------------------------

type Props = NavItemProps & {
  config: NavConfigProps;
};

const NavItem = forwardRef<HTMLDivElement, Props>(
  ({ item, depth, open, active, externalLink, config, ...other }, ref) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const handleOpenDialog = () => setDialogOpen(true);
    const handleCloseDialog = () => setDialogOpen(false);
    const { title, path, icon, children, disabled, caption, roles } = item;

    const handleLogout = async () => {
      try {
        await dispatch(doLogout()).unwrap();
        handleCloseDialog();
        navigate("/login");
      } catch (error) {
        console.error("Logout failed:", error);
      }
    };
    const subItem = depth !== 1;

    const renderContent = (
      <StyledItem
        disableGutters
        ref={ref}
        open={open}
        depth={depth}
        active={active}
        disabled={disabled}
        config={config}
        {...other}
        onClick={() => {
          if (title.toLowerCase() === "logout") {
            handleOpenDialog();
          }
        }}
      >
        {icon && <StyledIcon size={config.iconSize}>{icon}</StyledIcon>}

        {!(config.hiddenLabel && !subItem) && (
          <ListItemText
            sx={{
              width: 1,
              flex: "unset",
              ...(!subItem && {
                px: 0.5,
                mt: 0.5,
              }),
            }}
            primary={title}
            primaryTypographyProps={{
              noWrap: true,
              fontSize: 10,
              lineHeight: "16px",
              textAlign: "center",
              textTransform: "capitalize",
              fontWeight: active ? "fontWeightBold" : "fontWeightSemiBold",
              ...(subItem && {
                textAlign: "unset",
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
                fontWeight: active ? "fontWeightSemiBold" : "fontWeightMedium",
              }),
            }}
          />
        )}

        {caption && (
          <Tooltip title={caption} arrow placement="right">
            <Iconify
              width={16}
              icon="eva:info-outline"
              sx={{
                color: "text.disabled",
                ...(!subItem && {
                  top: 11,
                  left: 6,
                  position: "absolute",
                }),
              }}
            />
          </Tooltip>
        )}

        {!!children && (
          <Iconify
            width={16}
            icon="eva:arrow-ios-forward-fill"
            sx={{
              top: 11,
              right: 6,
              position: "absolute",
            }}
          />
        )}
      </StyledItem>
    );

    // Hidden item by role
    if (roles && !roles.includes(`${config.currentRole}`)) {
      return null;
    }

    // External link
    if (externalLink)
      return (
        <Link
          href={path}
          target="_blank"
          rel="noopener"
          underline="none"
          sx={{
            width: 1,
            ...(disabled && {
              cursor: "default",
            }),
          }}
        >
          {renderContent}
        </Link>
      );

    // Default
    return (
      <>
        <Link
          component={RouterLink}
          href={path}
          underline="none"
          sx={{
            width: 1,
            ...(disabled && {
              cursor: "default",
            }),
          }}
        >
          {renderContent}
        </Link>
        <LogoutDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleLogout}
        />
      </>
    );
  }
);

export default NavItem;
